/**
 * «Прожектор» во вьюере: ЕДИНСТВЕННОЕ место, где решается, показывать ли
 * продвижение (ADR 0019, PROMO §3).
 *
 * Почему одно место на все поверхности — карта, этаж, «Объём», поиск, «Объём
 * вся площадка». Правил ровно три, и разъехаться они не имеют права:
 *
 *  1. РЕКЛАМА ГАСНЕТ, КОГДА ЧЕЛОВЕК ИДЁТ. Состояния МАРШРУТ/ДВИЖЕНИЕ/АВАРИЯ —
 *     промо нет вовсе. Это не вежливость: карта «последней мили» существует,
 *     чтобы довести до цели, и реклама поперёк маршрута ломает сам продукт.
 *  2. СРОК ПРОВЕРЯЕТ КЛИЕНТ. Сервер отдаёт окно инстантами; между двумя
 *     запусками cron кампания не должна висеть лишние часы, а кешированный на
 *     пять минут ответ не должен показывать вчерашнее.
 *  3. ВЫДЕЛЕНИЕ НИКОГДА НЕ ПЕРЕБИВАЕТ БЕЗОПАСНОСТЬ И НАВИГАЦИЮ. Ранг —
 *     только через promoRank() из @app/rvf, где диапазоны разведены
 *     арифметически.
 */

import type { LocalizedText } from '@app/rvf'
import { type EmphasisPhase, type PromoTier, promoRank } from '@app/rvf'
import { computed, signal } from '@preact/signals'
import { emergencyHazard, liveOccupants, navProgress, routePlan } from './state'

export interface PromoEmphasis {
  readonly tier: PromoTier
  /** Кто оплатил. Обязателен по закону о рекламе — показывается в раскрытии. */
  readonly advertiser: string
  readonly active: { readonly from: string; readonly to: string }
  readonly house?: true
  readonly label?: LocalizedText
  readonly offer?: LocalizedText
  readonly logo?: string
  /** Значок кампании: percent | sale | new | gift | star (whitelist сервера). */
  readonly badge?: string
}

/**
 * Тик времени: раз в минуту, чтобы кампания гасла в свою полночь, а не при
 * следующей перезагрузке. Минута — компромисс: чаще незачем, реже заметно.
 */
const minuteTick = signal(0)
let timer: ReturnType<typeof setInterval> | null = null

export function startPromoClock(): void {
  if (timer !== null || typeof setInterval !== 'function') return
  timer = setInterval(() => {
    minuteTick.value = minuteTick.value + 1
  }, 60_000)
}

export function stopPromoClock(): void {
  if (timer !== null) {
    clearInterval(timer)
    timer = null
  }
}

/**
 * Состояние экрана. Маршрут построен или человек идёт — реклама гаснет;
 * авария гасит всё, кроме безопасности.
 */
export function promoPhase(): EmphasisPhase {
  if (emergencyHazard.value) return 'emergency'
  if (navProgress.value) return 'follow'
  if (routePlan.value) return 'route'
  return 'overview'
}

function withinWindow(emphasis: PromoEmphasis, now: number): boolean {
  const from = Date.parse(emphasis.active?.from ?? '')
  const to = Date.parse(emphasis.active?.to ?? '')
  // Битые даты не показываем: лучше не показать оплаченное (владелец
  // пожалуется, и мы починим) чем показать неоплаченное (пожалуется тот, кто
  // платил, и будет прав).
  if (!Number.isFinite(from) || !Number.isFinite(to)) return false
  return now >= from && now < to
}

/**
 * Активные выделения прямо сейчас: unit_id → блок. Пустая карта в состояниях
 * МАРШРУТ/ДВИЖЕНИЕ/АВАРИЯ — одной проверкой на все поверхности сразу.
 */
export const activePromos = computed<ReadonlyMap<string, PromoEmphasis>>(() => {
  void minuteTick.value
  const out = new Map<string, PromoEmphasis>()
  if (promoPhase() !== 'overview') return out
  const live = liveOccupants.value
  if (!live) return out
  const now = Date.now()
  for (const [unitId, occupant] of live) {
    const emphasis = (occupant as { emphasis?: PromoEmphasis }).emphasis
    if (emphasis && withinWindow(emphasis, now)) out.set(unitId, emphasis)
  }
  return out
})

/** Только уровни — для списков и поиска, где креатив не нужен. */
export const promoTiers = computed<ReadonlyMap<string, PromoTier>>(() => {
  const out = new Map<string, PromoTier>()
  for (const [unitId, promo] of activePromos.value) out.set(unitId, promo.tier)
  return out
})

/** Выделение конкретного юнита с учётом состояния экрана; null — не показывать. */
export function promoOf(unitId: string | null | undefined): PromoEmphasis | null {
  if (!unitId) return null
  return activePromos.value.get(unitId) ?? null
}

/** Ранг для сортировок и порядка отрисовки — только из общей формулы RVF. */
export function promoRankOf(unitId: string | null | undefined): number {
  const promo = promoOf(unitId)
  return promo ? promoRank(promo.tier, promoPhase()) : 0
}

/**
 * Блок сбоку показываем ОДИН — карусель из объявлений превращает карту в
 * рекламный щит, а платит за это внимание посетителя, который пришёл найти
 * дверь.
 *
 * КОГО ИМЕННО — РЕШАЕТ ЖРЕБИЙ, А НЕ ПОРЯДОК В ОТВЕТЕ СЕРВЕРА. Все, кто купил
 * один уровень, заплатили одинаково, и место сбоку обязано доставаться им по
 * очереди. Жребий детерминированный — от площадки и её локальной даты: за
 * сутки он не меняется (кампания не мигает при каждой перерисовке), а назавтра
 * очередь сдвигается. Порядок внутри уровня не продаётся.
 */
function dayHash(seed: string): number {
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  return hash
}

/** Локальная дата площадки из окна активной кампании — она уже в её таймзоне. */
function localDayOf(promos: ReadonlyMap<string, PromoEmphasis>): string {
  for (const promo of promos.values()) return promo.active.from.slice(0, 10)
  return ''
}

export const sidePromo = computed<{ unitId: string; promo: PromoEmphasis } | null>(() => {
  const promos = activePromos.value
  // Без креатива блок показывать нечем — остаётся только выделение на карте.
  const eligible = [...promos.entries()].filter(([, p]) => p.label || p.offer)
  if (eligible.length === 0) return null
  const top = Math.max(...eligible.map(([, p]) => promoRank(p.tier, 'overview')))
  const pool = eligible
    .filter(([, p]) => promoRank(p.tier, 'overview') === top)
    .sort((a, b) => a[0].localeCompare(b[0]))
  const pick = pool[dayHash(localDayOf(promos)) % pool.length]
  return pick ? { unitId: pick[0], promo: pick[1] } : null
})
